import moment from "moment-timezone";
import type { SalonService, StaffService } from "../../../../types/salon";

/** Drawer operating mode: new booking, in-place reschedule, or rebook. */
export type BookingMode = "create" | "edit" | "rebook";

/**
 * Where the session was started. Drives where closing returns to:
 * "popover" restores the stashed event preview popover; "calendar" returns
 * to the plain calendar.
 */
export type BookingOrigin = "calendar" | "popover";

/** Prefix of the calendar temp-event ids derived from draft rows. */
export const BOOKING_TEMP_ID_PREFIX = "booking-temp-";

export interface ServiceRow {
  uid: number;
  salonServiceId: string;
  employeeId: string;
  /**
   * The row's salon-local day ("YYYY-MM-DD"). In a booking draft every row
   * carries the draft's one day (bookingDraftReducer enforces it; a date
   * written on any row moves the whole draft).
   */
  date: string | null;
  time: string;
  /**
   * The UNADJUSTED base minutes: the provider's override, else the service
   * default, or the user's manual edit. Sent as `base_duration_mins` as is.
   * Never includes service-adjustment minutes (see adjustmentDuration).
   */
  baseDuration: number;
  processingTime: number;
  finishingTime: number;
  bufferTime: number;
  price: number;
  /**
   * Source identity for EDIT mode: the existing booked-service uuid and its
   * line-item uuid, carried so the in-place PUT /transactions/line-items can
   * target the right rows. Undefined for create/rebook (new rows).
   */
  sourceBookedServiceId?: string;
  sourceLineItemId?: string;
  /**
   * Selected service-adjustment option ids (one option per adjustment
   * question). Sent as `service_adjustment_form_option_ids`.
   */
  adjustmentOptionIds?: string[];
  /**
   * Minutes the selected adjustment options add (or remove): derived from
   * the option ids (draftModel.toggleAdjustmentOption) or, for a seeded row,
   * from the source's sub line items. Display / geometry only (row length,
   * end time, temp events, conflicts, stride): NEVER sent. The backend adds
   * each option's delta itself from the option id, on top of
   * base_duration_mins.
   */
  adjustmentDuration?: number;
  /**
   * EDIT mode: the option ids the source booked service was seeded with.
   * The reschedule write sends `service_adjustment_form_option_ids` only
   * when the row's selection differs from these.
   */
  sourceAdjustmentOptionIds?: string[];
}

export const emptyRow = (
  uid: number,
  date: string | null,
  time = "09:00",
): ServiceRow => ({
  uid,
  salonServiceId: "",
  employeeId: "",
  date,
  time,
  baseDuration: 30,
  processingTime: 0,
  finishingTime: 0,
  bufferTime: 0,
  price: 0,
  adjustmentOptionIds: [],
  adjustmentDuration: 0,
});

/**
 * The ONE source of a provider's configuration for a service: the salon
 * service's `servicing_staff` entry for that staff member (its id is the
 * staff_service_id to book with; it carries the per-staff price and
 * durations). CalendarContext.staffServices is only a projection of it
 * without those fields, so the booking flows never read it.
 */
export const staffServiceFor = (
  svc: SalonService | undefined,
  staffId: string,
): StaffService | undefined =>
  staffId
    ? (svc?.servicing_staff || []).find((ss) => ss.staff_id === staffId)
    : undefined;

/**
 * A salon service's price/durations for a given provider: that provider's
 * servicing_staff overrides where set, else the salon defaults. Empty staff
 * → salon defaults only.
 */
export const staffServiceFields = (
  svc: SalonService | undefined,
  staffId: string,
): Pick<
  ServiceRow,
  "baseDuration" | "processingTime" | "finishingTime" | "bufferTime" | "price"
> => {
  const ov = staffServiceFor(svc, staffId);
  return {
    baseDuration:
      ov?.base_duration_mins ?? svc?.default_base_duration_mins ?? 30,
    processingTime:
      ov?.processing_time_mins ?? svc?.default_processing_time_mins ?? 0,
    finishingTime:
      ov?.finishing_time_mins ?? svc?.default_finishing_time_mins ?? 0,
    bufferTime: ov?.buffer_time_mins ?? svc?.default_buffer_time_mins ?? 0,
    price: ov?.price ?? svc?.default_price ?? 0,
  };
};

/** Every row on the one booking day (single-day rule). */
export const withDay = (rows: ServiceRow[], day: string): ServiceRow[] =>
  rows.map((r) => (r.date === day ? r : { ...r, date: day }));

/**
 * The day a rows update asked for, if it differs from the draft's day (the
 * form changes the day by setting it on the rows). null = day unchanged.
 */
export const requestedDay = (
  rows: ServiceRow[],
  currentDay: string,
): string | null =>
  rows.find((r) => r.date && r.date !== currentDay)?.date ?? null;

/** "YYYY-MM-DD" of a CalendarContext date (a browser-local calendar day). */
export const dayOf = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** CalendarContext date for a "YYYY-MM-DD" day (browser-local midnight). */
export const dateOfDay = (day: string): Date => {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y, m - 1, d);
};

/**
 * Whether a draft day is on screen: the viewed day in day view, the viewed
 * week (moment's locale week, as the RBC momentLocalizer uses) in week view.
 * Temp events render only then.
 */
export const isDayInView = (
  day: string,
  viewType: string | undefined,
  viewedDate: Date,
): boolean => {
  if (!day) return false;
  if (viewType !== "week") return day === dayOf(viewedDate);
  const start = moment(viewedDate).startOf("week").format("YYYY-MM-DD");
  const end = moment(viewedDate).endOf("week").format("YYYY-MM-DD");
  return day >= start && day <= end;
};
